import "diff2html/bundles/css/diff2html.min.css";

import "./diff-viewer.css";
import "./monaco-style-overwrites.css";

import { atom, useAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { debounce } from "lodash";
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { useAutoHideRemoteUserSelection } from "@/features/model-tool";
import { useOpenModelInTab } from "@/features/model-tool";
import { useLatestValueRef } from "@/hooks/useLatestValueRef";
import { TrackRecentModels_Detached } from "@/hooks/useRecentModels";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useDarkMode } from "@/providers/DarkModeProvider";
import { useToast } from "@/providers/ToastProvider";
import { datadogRum } from "@datadog/browser-rum";
import Editor from "@monaco-editor/react";

import {
  useGetEditorTextState,
  useModelEditorDispatch,
  useModelEditorState,
  useModelEditorTextState,
} from "../ModelEditorStore";
import { EDITOR_TAB_SIZE } from "../constants/editor";
import { useFireMonaco, useUpdateFireMonacoText } from "../hooks/useFireMonaco";
import { useKeyEventHandler } from "../hooks/useKeyboardShortcuts";
import { useModelDepedenciesMapGetter } from "../providers/ModelDependenciesProvider";
import { useExecuteSql } from "./ActionButtons";
import AutoSaveNotification from "./AutoSaveNotification";
import useCompletionItems from "./completion-suggestions/useCompletionItems";
import { useHighlightWeldTags } from "./monaco/useHighlighWeldTags";
import useModelMarkers from "./monaco/useModelMarkers";
import { useMonacoActions } from "./monaco/useMonacoActions";
import { useWeldTagClickHandler } from "./monaco/useWeldTagClickHandler";
import { useWeldTagHoverDialog } from "./monaco/useWeldTagHoverDialog";
import { useModelBackupDraft, useUpdateDraftModel } from "./useModelDraft";

const editorProps: monaco.editor.IEditorOptions &
  monaco.editor.IDiffEditorBaseOptions = {
  minimap: {
    enabled: false,
  },
  automaticLayout: true,
  scrollbar: {
    verticalScrollbarSize: 0,
  },
  hideCursorInOverviewRuler: true,
  renderLineHighlight: "none",
  scrollBeyondLastLine: false,
  padding: { top: 20 },
  renderOverviewRuler: false,
  enableSplitViewResizing: false,
  renderIndicators: false,
  fixedOverflowWidgets: true,
};

const selectedTextAtom = atom<string>("");
export const useSelectedText = () => useAtom(selectedTextAtom);

export const useGetSelectedText = () => {
  return useAtomCallback(
    useCallback((get) => {
      const value = get(selectedTextAtom);
      return value;
    }, []),
  );
};

const monacoInstanceAtom = atom<monaco.editor.IStandaloneCodeEditor | null>(
  null,
);
export const useMonacoInstance = () => useAtom(monacoInstanceAtom);

export default function SQLEditor() {
  const { isDarkModeEnabled } = useDarkMode();
  const state = useModelEditorState();
  const dispatch = useModelEditorDispatch();
  const toast = useToast();
  const mixpanel = useMixpanel();

  const getTextState = useGetEditorTextState();
  const createModelBackupDraft = useModelBackupDraft();

  const { enableForceSyncOnAutosaveFail } = useFlags();

  const [, setMonacoInstance] = useMonacoInstance();

  const updateCurrentDraftModel = useUpdateDraftModel(state.currentModelId);

  const updateDraftDebounced = useDebouncedCallback(
    updateCurrentDraftModel,
    500,
  );

  const handleChangeSqlQuery = (query: string) => {
    dispatch({ type: "change_sql_query", payload: query });
    if (state.currentModelType === "draft") {
      updateDraftDebounced(query);
    }
  };

  useAutoHideRemoteUserSelection(state.currentModelId);

  const updateLiveText = useUpdateFireMonacoText();

  // A ref to store the last forced content for the editor.
  // Used to prevent (potential) infinite loops when the editor is empty and a forced update is attempted.
  const lastForcedContentRef = useRef<string | null>(null);

  const { connect: connectToFirebase, isConnected } = useFireMonaco({
    onReady: (text, setText) => {
      const editorTextState = getTextState();

      function hasWindowsLineEndings(str: string) {
        return str.includes("\r\n");
      }

      if (hasWindowsLineEndings(text)) {
        mixpanel.track("Editor: Windows Line Endings Detected", {
          source: "firebase",
          model_id: state.currentModelId,
          model_name: state.modelName,
        });
      }

      if (hasWindowsLineEndings(editorTextState.defaultSql)) {
        mixpanel.track("Editor: Windows Line Endings Detected", {
          source: "published-query",
          model_id: state.currentModelId,
          model_name: state.modelName,
        });
      }

      if (text === "" && editorTextState.defaultSql) {
        setText(editorTextState.defaultSql);
      }

      handleChangeSqlQuery(text);
    },
    onSynced: (text) => {
      handleChangeSqlQuery(text);
    },
    onError(error) {
      const editorContents = editorRef.current?.getModel()?.getValue();
      if (!editorContents || editorContents.trim() === "") {
        // The editor is empty, no need to create a draft
        return;
      }

      // Check against the cached last forced content.
      if (
        lastForcedContentRef.current !== null &&
        editorContents === lastForcedContentRef.current
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          "Backup and Forced update already attempted with these contents; skipping to avoid (potential) infinite loop.",
        );
        return;
      }

      mixpanel.track("Autosave Failed Notification Triggered", {
        model_id: state.currentModelId,
        model_name: state.modelName,
        error_name: error.err.name,
        error_message: error.err.message,
      });

      datadogRum.addAction("Editor: Firebase-Monaco Error", {
        model_id: state.currentModelId,
        model_name: state.modelName,
        error_name: error.err.name,
        error_message: error.err.message,
      });
      datadogRum.startSessionReplayRecording();

      // Cache the current editor contents as the last forced content.
      lastForcedContentRef.current = editorContents;

      if (enableForceSyncOnAutosaveFail) {
        // Synchronously force update the live text in Firebase.
        updateLiveText(editorContents);
      }

      // Create a local draft model as a backup
      const draft = createModelBackupDraft(editorContents, state.modelName);

      // Notify the user about the issue.
      toast(
        "Your changes may not be saved!",
        <div className="space-y-2">
          <p>
            We ran into an error while auto-saving your model. A local backup
            has been created as <strong>{draft.name}</strong> as a precaution,
            and we’re retrying to sync your changes in the background.
          </p>
          <p>
            To ensure everything is up to date and the connection is restored,
            please reload the page. This will also help you verify whether any
            changes were lost.
          </p>
        </div>,
        "error",
        null,
      );
    },
  });

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();

  const [editorLoaded, setEditorLoaded] = useState(false);

  const handleKeyEventRef = useLatestValueRef(useKeyEventHandler());

  const handleMountEditor = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      // Use line feed (\n) as the end of line character.
      // This is important for collaborative editing to prevent sync-issues between different browsers & platforms.
      editor.getModel()?.setEOL(monaco.editor.EndOfLineSequence.LF);
      // Set editor tab size so that it aligns with our formatter config.
      editor.getModel()?.updateOptions({ tabSize: EDITOR_TAB_SIZE });
      editor.focus();
      editorRef.current = editor;
      setMonacoInstance(editor);
      setEditorLoaded(true);

      editor.onKeyDown((e) => {
        handleKeyEventRef.current(e.browserEvent);
      });

      if (state.currentModelType === "model") {
        connectToFirebase(editor, state.currentModelId ?? "");
      }
    },
    [
      setMonacoInstance,
      state.currentModelType,
      state.currentModelId,
      connectToFirebase,
      handleKeyEventRef,
    ],
  );

  useEffect(
    () => () => {
      setEditorLoaded(false);
      setMonacoInstance(null);
    },
    [setMonacoInstance],
  );

  const [, setSelectedText] = useSelectedText();
  const updateSelectedText = useCallback(() => {
    const editor = editorRef.current;
    if (editor) {
      const selection = editor.getSelection();
      if (!selection) return;
      const text = editor.getModel()?.getValueInRange({
        startLineNumber: selection.startLineNumber,
        startColumn: selection.startColumn,
        endLineNumber: selection.endLineNumber,
        endColumn: selection.endColumn,
      });
      setSelectedText(text ?? "");
      return;
    }

    setSelectedText("");
  }, [setSelectedText]);

  useEffect(() => {
    if (!editorLoaded) return;

    const debouncedUpdateSelectedText = debounce(updateSelectedText, 100);
    const disposable = editorRef.current?.onDidChangeCursorSelection(() => {
      debouncedUpdateSelectedText();
    });
    return () => {
      disposable?.dispose();
      setSelectedText("");
    };
  }, [editorLoaded, setSelectedText, updateSelectedText]);

  const handleExecute = useExecuteSql();

  useMonacoActions(editorRef.current, [
    {
      id: "execute-query",
      label: "Execute Query",
      run: () => {
        handleExecute();
      },
    },
  ]);

  useCompletionItems();
  useModelMarkers(editorRef);
  useHighlightWeldTags(editorRef.current);
  useWeldTagHoverDialog(editorRef.current);

  return (
    <div className="relative flex h-full min-w-0 flex-col overflow-hidden">
      {!isConnected && state.currentModelType === "model" && (
        <div className="absolute inset-0 z-50 ml-px grid place-items-center bg-background">
          <LoadingSpinner />
        </div>
      )}
      <div className="relative flex h-full grow flex-col overflow-hidden">
        <Editor
          path={state.currentModelId ?? ""}
          theme={isDarkModeEnabled ? "vs-dark" : "vs"}
          defaultValue={getTextState().weldSql}
          loading={<div />}
          language="sql"
          options={editorProps}
          onMount={handleMountEditor}
          onChange={(value) => {
            if (
              ["draft", "tile"].includes(state.currentModelType) &&
              typeof value === "string"
            ) {
              handleChangeSqlQuery(value);
            }
          }}
        />
        <EditorPlaceholderOverlay />
      </div>
      <AutoSaveNotification />
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <TrackRecentModels_Detached />
      <WeldTagClickListener
        editorRef={editorRef.current}
        modelId={state.currentModelId}
      />
    </div>
  );
}

function WeldTagClickListener(props: {
  modelId: string;
  editorRef: monaco.editor.IStandaloneCodeEditor | undefined;
}) {
  const openModelInTab = useOpenModelInTab();
  const getDependenciesMap = useModelDepedenciesMapGetter();

  useWeldTagClickHandler(props.editorRef, (event, { weldTag }) => {
    if (event.metaKey) {
      const dwItem = getDependenciesMap().get(weldTag);

      if (!dwItem) {
        return;
      }
      if (dwItem.__typename === "RawViewReference") {
        return;
      }

      const modelId =
        dwItem.__typename === "MaterializedTableReference"
          ? dwItem.modelId
          : dwItem.__typename === "ModelViewReference"
            ? dwItem.modelId
            : null;

      if (modelId) {
        openModelInTab({ modelId });
      }
    }
  });
  return null;
}

const EditorPlaceholderOverlay = memo(() => {
  const { weldSql } = useModelEditorTextState();
  if (!weldSql) {
    return (
      <div
        className="pointer-events-none absolute left-0 top-0 text-gray-300 dark:text-gray-500"
        style={{
          marginTop: "17px",
          marginLeft: "64px",
        }}
      >
        {"select * from {{raw.source.table}}"}
      </div>
    );
  }
  return null;
});
